import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { buildShopScopedPath, withShopIdPayload } from '../../../utils/adminShopContext';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const NotificationPreferencesSettings = () => {
  const { shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);

  // Shop-level notification delivery toggles
  const [shopSettings, setShopSettings] = useState({
    notify_email_enabled: true,
    notify_sms_enabled: false,
    admin_email: '',
    admin_phone: ''
  });
  const [savingShop, setSavingShop] = useState(false);

  // Load preferences and defaults
  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const [prefsResponse, defaultsResponse, shopResponse] = await Promise.all([
        axiosClient.get('/settings/notifications'),
        axiosClient.get('/settings/notifications/defaults'),
        axiosClient.get(buildShopScopedPath('/admin/settings/shop', shopId))
      ]);

      // Defaults drive structure
      let defaults = null;
      if (defaultsResponse.data?.success) {
        defaults = defaultsResponse.data.data.defaults;
        setAvailableEvents(defaultsResponse.data.data.availableEvents);
        setAvailableChannels(defaultsResponse.data.data.availableChannels);
      }

      // Merge defaults + stored preferences (handles older/malformed rows)
      const storedPrefs = prefsResponse.data?.success ? prefsResponse.data.data.preferences : null;
      const merged = {
        ...(defaults || {}),
        ...(storedPrefs || {}),
        channels: {
          ...(defaults?.channels || {}),
          ...(storedPrefs?.channels || {})
        },
        events: {
          ...(defaults?.events || {}),
          ...(storedPrefs?.events || {})
        }
      };

      // If we couldn't load defaults, still set whatever we have
      setPreferences(defaults ? merged : storedPrefs);

      if (shopResponse?.data) {
        setShopSettings(prev => ({
          ...prev,
          notify_email_enabled: !!shopResponse.data.notify_email_enabled,
          notify_sms_enabled: !!shopResponse.data.notify_sms_enabled,
          admin_email: shopResponse.data.admin_email || '',
          admin_phone: shopResponse.data.admin_phone || ''
        }));
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      toast.error(t('admin.settings_loading_notif_prefs'));
    } finally {
      setLoading(false);
    }
  }, [shopId, t]);

  // Save preferences
  const savePreferences = async () => {
    try {
      setSaving(true);
      await axiosClient.post('/settings/notifications', { preferences });
      toast.success(t('admin.settings_notif_prefs_saved'));
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      const errorMessage = error.response?.data?.error || t('admin.settings_loading_notif_prefs');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = async () => {
    if (!window.confirm(t('admin.settings_notif_reset_confirm'))) {
      return;
    }

    try {
      setSaving(true);
      const response = await axiosClient.post('/settings/notifications/reset');
      if (response.data.success) {
        setPreferences(response.data.data.preferences);
        toast.success(t('admin.settings_notif_prefs_reset'));
      }
    } catch (error) {
      console.error('Error resetting notification preferences:', error);
      toast.error(t('admin.settings_loading_notif_prefs'));
    } finally {
      setSaving(false);
    }
  };

  // Save shop-level delivery toggles
  const saveShopSettings = async () => {
    try {
      setSavingShop(true);
      await axiosClient.put(buildShopScopedPath('/admin/settings/shop', shopId), withShopIdPayload({
        notify_email_enabled: shopSettings.notify_email_enabled,
        notify_sms_enabled: shopSettings.notify_sms_enabled,
        admin_email: shopSettings.admin_email || null,
        admin_phone: shopSettings.admin_phone || null
      }, shopId));
      toast.success(t('admin.settings_shop_notif_saved'));
    } catch (error) {
      console.error('Error saving shop notification settings:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_error_saving');
      toast.error(errorMessage);
    } finally {
      setSavingShop(false);
    }
  };

  // Toggle channel for a specific event
  const toggleEventChannel = (eventKey, channel) => {
    if (!preferences) {
      console.warn('Preferences not loaded yet');
      return;
    }

    if (channel === 'email' && !shopSettings.notify_email_enabled) {
      toast.error(t('admin.settings_email_disabled_warning'));
      return;
    }

    setPreferences(prev => {
      const base = prev || {};
      const newPrefs = JSON.parse(JSON.stringify(base)); // Deep clone

      if (!newPrefs.events || typeof newPrefs.events !== 'object') {
        newPrefs.events = {};
      }

      const current = Array.isArray(newPrefs.events[eventKey]) ? newPrefs.events[eventKey] : [];

      if (current.includes(channel)) {
        newPrefs.events[eventKey] = current.filter(c => c !== channel);
      } else {
        newPrefs.events[eventKey] = [...current, channel];
      }

      return newPrefs;
    });
  };

  // Check if a channel is enabled for an event
  const isChannelEnabled = (eventKey, channel) => {
    if (!preferences || !preferences.events) return false;
    const eventChannels = preferences.events[eventKey] || [];
    return eventChannels.includes(channel);
  };

  // Toggle all channels for an event
  const toggleAllChannelsForEvent = (eventKey) => {
    if (!preferences) return;
    
    setPreferences(prev => {
      const newPrefs = JSON.parse(JSON.stringify(prev)); // Deep clone
      const eventChannels = newPrefs.events[eventKey] || [];
      
      if (eventChannels.length === availableChannels.length) {
        // All enabled, disable all
        newPrefs.events[eventKey] = [];
      } else {
        // Enable all
        newPrefs.events[eventKey] = availableChannels.map(c => c.key);
      }
      
      return newPrefs;
    });
  };

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
          <CircularProgress size={32} style={{ color: ACCENT }} />
          <span>{t('admin.settings_loading_notif_prefs')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader
          title={t('admin.settings_notif_prefs_title')}
          subtitle={t('admin.settings_notif_prefs_subtitle')}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          }
        />
        <div className="p-4 md:p-5">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <p className="font-medium mb-1">{t('admin.settings_notif_about_title')}</p>
                <p className="text-blue-800 dark:text-blue-200">
                  {t('admin.settings_notif_about_text')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Shop Delivery Card */}
      <Card>
        <CardHeader
          title={t('admin.settings_shop_delivery_title')}
          subtitle={t('admin.settings_shop_delivery_subtitle')}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
          action={
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={saveShopSettings}
              disabled={savingShop}
              className="px-4 py-2 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ background: ACCENT }}
              type="button"
            >
              {savingShop ? t('admin.settings_saving') : t('admin.settings_save')}
            </motion.button>
          }
        />
        <div className="p-4 md:p-5 space-y-4">
          {!shopSettings.notify_email_enabled && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('admin.settings_email_disabled_warning')}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center justify-between gap-3 p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-white/10">
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{t('admin.settings_email_notif')}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('admin.settings_email_notif_hint')}</p>
              </div>
              <input
                type="checkbox"
                checked={!!shopSettings.notify_email_enabled}
                onChange={(e) => setShopSettings(prev => ({ ...prev, notify_email_enabled: e.target.checked }))}
              />
            </label>

            <label className="flex items-center justify-between gap-3 p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-white/10">
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{t('admin.settings_sms_notif')}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('admin.settings_sms_notif_hint')}</p>
              </div>
              <input
                type="checkbox"
                checked={!!shopSettings.notify_sms_enabled}
                onChange={(e) => setShopSettings(prev => ({ ...prev, notify_sms_enabled: e.target.checked }))}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.settings_admin_email')}</label>
              <input
                type="email"
                value={shopSettings.admin_email}
                onChange={(e) => setShopSettings(prev => ({ ...prev, admin_email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
                placeholder="owner@yourshop.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.settings_admin_phone')}</label>
              <input
                type="tel"
                value={shopSettings.admin_phone}
                onChange={(e) => setShopSettings(prev => ({ ...prev, admin_phone: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
                placeholder="+1..."
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Events Card */}
      <Card>
        <CardHeader
          title={t('admin.settings_notif_events_title')}
          subtitle={t('admin.settings_notif_events_subtitle', { count: availableEvents.length })}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
        />
        <div className="p-4 md:p-5">
          {/* Channel Headers */}
          <div className="mb-4 pb-4 border-b border-zinc-200 dark:border-white/10">
            <div className="grid grid-cols-12 gap-4 items-center text-sm font-medium text-zinc-600 dark:text-zinc-400">
              <div className="col-span-6">{t('admin.settings_event_col')}</div>
              {availableChannels.map(channel => (
                <div key={channel.key} className="col-span-3 text-center">
                  {channel.label}
                </div>
              ))}
            </div>
          </div>

          {/* Event Rows */}
          <div className="space-y-3">
            {!preferences && (
              <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                {t('admin.settings_loading_prefs')}
              </div>
            )}
            {preferences && availableEvents.map((event, index) => (
              <motion.div
                key={event.key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="grid grid-cols-12 gap-4 items-center p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-white/10 hover:border-[--accent]/30 dark:hover:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <div className="col-span-6">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleAllChannelsForEvent(event.key)}
                      className="mt-1 flex-shrink-0 cursor-pointer"
                      type="button"
                      title={t('admin.settings_toggle_all')}
                    >
                      <div className={`h-5 w-5 rounded border-2 transition-all flex items-center justify-center ${
                        (preferences?.events?.[event.key]?.length || 0) === availableChannels.length
                          ? 'bg-[--accent] border-[--accent]'
                          : (preferences?.events?.[event.key]?.length || 0) > 0
                          ? 'bg-zinc-300 dark:bg-zinc-600 border-zinc-300 dark:border-zinc-600'
                          : 'border-zinc-300 dark:border-zinc-600'
                      }`} style={{ '--accent': ACCENT }}>
                        {(preferences?.events?.[event.key]?.length || 0) > 0 && (
                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                    <div>
                      <p className="font-medium text-zinc-900 dark:text-zinc-100">{event.label}</p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{event.description}</p>
                    </div>
                  </div>
                </div>
                
                {availableChannels.map(channel => (
                  <div key={channel.key} className="col-span-3 flex justify-center">
                    <button
                      onClick={() => toggleEventChannel(event.key, channel.key)}
                      className="group cursor-pointer"
                      type="button"
                      title={`${channel.label} — ${event.label}`}
                    >
                      <div className={`h-6 w-6 rounded border-2 transition-all flex items-center justify-center group-hover:scale-110 cursor-pointer ${
                        isChannelEnabled(event.key, channel.key)
                          ? 'bg-[--accent] border-[--accent]'
                          : 'border-zinc-300 dark:border-zinc-600 group-hover:border-[--accent]/50'
                      }`} style={{ '--accent': ACCENT }}>
                        {isChannelEnabled(event.key, channel.key) && (
                          <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  </div>
                ))}
              </motion.div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-zinc-200 dark:border-white/10 flex items-center justify-between gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetToDefaults}
              disabled={saving}
              className="px-4 py-2 rounded-xl border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('admin.settings_reset_defaults')}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={savePreferences}
              disabled={saving}
              className="px-6 py-2 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ background: ACCENT }}
            >
              {saving ? (
                <>
                  <CircularProgress size={16} style={{ color: 'white' }} />
                  {t('admin.settings_saving_ellipsis')}
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('admin.settings_save_changes')}
                </>
              )}
            </motion.button>
          </div>
        </div>
      </Card>

      {/* Channel Legend */}
      <Card>
        <CardHeader
          title={t('admin.settings_channel_info_title')}
          subtitle={t('admin.settings_channel_info_subtitle')}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <div className="p-4 md:p-5">
          <div className="space-y-3">
            {availableChannels.map(channel => (
              <div key={channel.key} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/30">
                <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
                  {channel.key === 'in_app' ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium text-zinc-900 dark:text-zinc-100">{channel.label}</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">{channel.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default NotificationPreferencesSettings;
